import { useState } from "react";
import { useApp } from "../App";
import api from "../services/api";
import "../styles/ContactPage.css";
import {
  HiLocationMarker,
  HiPhone,
  HiMail,
  HiClock,
  HiTruck,
  HiArrowRight,
} from "react-icons/hi";
import {
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaShippingFast,
} from "react-icons/fa";
import { MdSupportAgent } from "react-icons/md";

export default function ContactPage() {
  const { showToast } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [focused, setFocused] = useState(null);

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      showToast("Please fill all required fields.", "error");
      return;
    }
    setSending(true);
    try {
      await api.contact.send(form);
      showToast("Message sent! We'll respond within 24 hours. 📩");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      showToast(err.message || "Failed to send message.", "error");
    } finally {
      setSending(false);
    }
  };

  const contactDetails = [
    {
      icon: <HiLocationMarker size={20} />,
      title: "Atelier Address",
      text: (
        <>
          K.K.Balusamy & CO<br />
          N.10, Velappangoundan Palayam Valasu,<br />
          Kaikattivalasu Villarasampatti<br />
          Tamil Nadu 638107, Erode
        </>
      ),
    },
    {
      icon: <HiPhone size={20} />,
      title: "Direct Line",
      text: "+91-98432 22262 · 0424-2226264",
    },
    {
      icon: <HiMail size={20} />,
      title: "Correspondence",
      text: "kkblungies@gmail.com",
    },
    {
      icon: <HiClock size={20} />,
      title: "Studio Hours",
      text: "Monday – Saturday · 9:00 AM to 6:00 PM IST",
    },
  ];

  return (
    <div className="kkb-contact-root">

      {/* ── HERO ── */}
      <header className="kkb-hero">
        <div className="kkb-hero__noise" />
        <div className="kkb-hero__line kkb-hero__line--top" />
        <div className="kkb-hero__line kkb-hero__line--bottom" />
        <div className="kkb-hero__inner">
          <span className="kkb-hero__eyebrow">K.K. Balusamy &amp; Co.</span>
          <h1 className="kkb-hero__title">
            <span className="kkb-hero__title-serif">Contact</span>
            <span className="kkb-hero__title-block">US</span>
          </h1>
          <p className="kkb-hero__sub">
            We'd love to hear from you — reach out anytime.
          </p>
        </div>
        <div className="kkb-hero__scroll-hint">
          <span />
        </div>
      </header>

      {/* ── MAP ── */}
      <div className="kkb-map-wrap">
        <div className="kkb-map-label">
          <span>Our Location</span>
          <HiArrowRight size={14} />
        </div>
        <iframe
          title="KK Balusamy & Co Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3911.898406504647!2d77.67951747575668!3d11.34213074850307!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba96f46b190264f%3A0x39e07ff88b4b19d5!2sK%20K%20Balusamy%20%26%20Co%20-%20Premium%20Printed%20Lungi%20Manufacturers%20in%20Erode!5e0!3m2!1sen!2sin!4v1702299179062!5m2!1sen!2sin"
          width="100%"
          height="420"
          style={{ border: 0, display: "block", filter: "grayscale(20%) contrast(1.04)" }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      {/* ── MAIN TWO-COL ── */}
      <section className="kkb-section">
        <div className="kkb-container">
          <div className="kkb-grid">

            {/* LEFT — INFO */}
            <aside className="kkb-info">
              <div className="kkb-info__header">
                {/* <div className="kkb-rule" /> */}
                <h2 className="kkb-info__heading">Get In Touch</h2>
              </div>
              <p className="kkb-info__intro">
                Questions about our collections, an ongoing order, or wholesale
                enquiries? Our concierge team is at your service.
              </p>

              <ul className="kkb-details">
                {contactDetails.map((d) => (
                  <li className="kkb-details__item" key={d.title}>
                    <span className="kkb-details__icon">{d.icon}</span>
                    <div>
                      <h4 className="kkb-details__label">{d.title}</h4>
                      <p className="kkb-details__value">{d.text}</p>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Decorative quote */}
              <blockquote className="kkb-quote">
                "Woven with precision,<br />delivered with pride."
              </blockquote>
            </aside>

            {/* RIGHT — FORM */}
            <div className="kkb-form-shell">
              <div className="kkb-form-header">
                {/* <div className="kkb-rule" /> */}
                <h2 className="kkb-form-title">Send Us an Email</h2>
              </div>

              <form onSubmit={handleSubmit} className="kkb-form" noValidate>
                <div className="kkb-form__row">
                  <div className={`kkb-field ${focused === "name" ? "kkb-field--active" : ""} ${form.name ? "kkb-field--filled" : ""}`}>
                    <label className="kkb-field__label">Your Name <span>*</span></label>
                    <input
                      className="kkb-field__input"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      onFocus={() => setFocused("name")}
                      onBlur={() => setFocused(null)}
                      placeholder="Full name"
                      required
                    />
                    <span className="kkb-field__bar" />
                  </div>
                  <div className={`kkb-field ${focused === "email" ? "kkb-field--active" : ""} ${form.email ? "kkb-field--filled" : ""}`}>
                    <label className="kkb-field__label">Email Address <span>*</span></label>
                    <input
                      className="kkb-field__input"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      onFocus={() => setFocused("email")}
                      onBlur={() => setFocused(null)}
                      placeholder="you@example.com"
                      required
                    />
                    <span className="kkb-field__bar" />
                  </div>
                </div>

                <div className="kkb-form__row">
                  <div className={`kkb-field ${focused === "phone" ? "kkb-field--active" : ""} ${form.phone ? "kkb-field--filled" : ""}`}>
                    <label className="kkb-field__label">Phone Number</label>
                    <input
                      className="kkb-field__input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      onFocus={() => setFocused("phone")}
                      onBlur={() => setFocused(null)}
                      placeholder="+91 XXXXX XXXXX"
                    />
                    <span className="kkb-field__bar" />
                  </div>
                  <div className={`kkb-field ${focused === "company" ? "kkb-field--active" : ""} ${form.company ? "kkb-field--filled" : ""}`}>
                    <label className="kkb-field__label">Company / Organisation</label>
                    <input
                      className="kkb-field__input"
                      value={form.company}
                      onChange={(e) => handleChange("company", e.target.value)}
                      onFocus={() => setFocused("company")}
                      onBlur={() => setFocused(null)}
                      placeholder="Your company"
                    />
                    <span className="kkb-field__bar" />
                  </div>
                </div>

                <div className={`kkb-field kkb-field--full ${focused === "message" ? "kkb-field--active" : ""} ${form.message ? "kkb-field--filled" : ""}`}>
                  <label className="kkb-field__label">Your Message <span>*</span></label>
                  <textarea
                    className="kkb-field__input kkb-field__input--textarea"
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    onFocus={() => setFocused("message")}
                    onBlur={() => setFocused(null)}
                    placeholder="Tell us about your enquiry…"
                    required
                  />
                  <span className="kkb-field__bar" />
                </div>

                <button
                  type="submit"
                  className={`kkb-submit ${sending ? "kkb-submit--sending" : ""}`}
                  disabled={sending}
                >
                  <span className="kkb-submit__text">
                    {sending ? "Sending…" : "Ask a Question"}
                  </span>
                  <span className="kkb-submit__arrow">
                    <HiArrowRight size={18} />
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <section className="kkb-trust">
        <div className="kkb-container">
          <div className="kkb-trust__grid">
            {[
              { icon: <MdSupportAgent size={28} />, title: "Dedicated Support", sub: "Response within 24 hours" },
              { icon: <FaShippingFast size={28} />, title: "Free Standard Shipping", sub: "On all orders across India" },
              { icon: <HiTruck size={28} />, title: "Pan India Delivery", sub: "Every corner, every state" },
            ].map((t) => (
              <div className="kkb-trust__card" key={t.title}>
                <span className="kkb-trust__icon">{t.icon}</span>
                <strong className="kkb-trust__name">{t.title}</strong>
                <p className="kkb-trust__sub">{t.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section className="kkb-cta">
        <div className="kkb-cta__noise" />
        <div className="kkb-container kkb-cta__inner">
          <p className="kkb-cta__question">
            Do you have questions about how we can help your company?
          </p>
          <a
            href="#"
            className="kkb-cta__link"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector(".kkb-form-shell")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Send us an email and we'll get in touch shortly
            <HiArrowRight size={16} style={{ marginLeft: 8 }} />
          </a>
        </div>
      </section>

      {/* ── SOCIAL ── */}
      <section className="kkb-social">
        <div className="kkb-container kkb-social__inner">
          <div className="kkb-social__header">
            {/* <div className="kkb-rule" /> */}
            <h3 className="kkb-social__heading">Follow the Craft</h3>
            <p className="kkb-social__sub">Stay updated with our latest collections and offers.</p>
          </div>
          <div className="kkb-social__links">
            {[
              { icon: <FaInstagram size={22} />, label: "@kkb_lungies", platform: "Instagram", color: "#E4405F", href: "https://instagram.com" },
              { icon: <FaFacebook size={22} />, label: "KK Balusamy & Co.", platform: "Facebook", color: "#1877F2", href: "https://facebook.com" },
              { icon: <FaYoutube size={22} />, label: "KK Balusamy & Co.", platform: "YouTube", color: "#FF0000", href: "https://youtube.com" },
            ].map((s) => (
              <a
                key={s.platform}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                className="kkb-social__card"
              >
                <span className="kkb-social__card-icon" style={{ color: s.color }}>{s.icon}</span>
                <span className="kkb-social__card-platform">{s.platform}</span>
                <span className="kkb-social__card-handle">{s.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}